import React, { useState, useContext } from "react"
import { Elements, useStripe, useElements, CardNumberElement, CardExpiryElement, CardCvcElement } from "@stripe/react-stripe-js"

import { AlertsContext } from "../../../utils/providers/alerts"
import { stripePromise } from "../../../utils/stripe"
import { funEU } from "../../../utils/firebase"

import Window from "../../structure/window/window"
import WindowCard from "../../ui/window-card/window-card"
import Input from "../../ui/inputs/input"
import Button from "../../ui/button/button"
import RecipientPicker from "../recipient-picker/recipient-picker"

import styles from "./take-card.module.scss"

const createPaymentIntentCallable = funEU.httpsCallable("createPaymentIntent")

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const formatGbp = (value) =>
	Number(value || 0).toLocaleString("en-GB", {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	})

const ELEMENT_OPTIONS = {
	style: {
		base: {
			fontSize: "14px",
			color: "#101828",
			fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
			"::placeholder": { color: "#98a2b3" },
		},
		invalid: { color: "#b42318" },
	},
}

/**
 * Wrapper that pulls in the Elements provider. Stripe.js is loaded lazily on
 * first import via the shared `stripePromise` singleton.
 */
export default function TakeCard(props) {
	if (!stripePromise) {
		return (
			<Window
				title="Take card (MOTO)"
				className="slim"
				close={() => props.close()}>
				<WindowCard>
					<p style={{ fontSize: 13, color: "#b42318" }}>Stripe publishable key not configured. Set REACT_APP_STRIPE_PUBLISHABLE_KEY in your environment.</p>
				</WindowCard>
			</Window>
		)
	}

	return (
		<Elements
			stripe={stripePromise}
			options={ELEMENTS_OPTIONS}>
			<TakeCardForm {...props} />
		</Elements>
	)
}

// Suppress Stripe Link autofill ("Save with Link" prompt) inside the card
// fields — we're not using Link as a payment method, and it confuses staff
// who are typing card details on behalf of a client over the phone.
const ELEMENTS_OPTIONS = {
	mode: "payment",
	currency: "gbp",
	amount: 100,
	paymentMethodCreation: "manual",
	paymentMethodTypes: ["card"],
}

function TakeCardForm({ bookingID, outstanding, leadBooker, close, onFallbackToLink }) {
	const stripe = useStripe()
	const elements = useElements()
	const { pushAlert } = useContext(AlertsContext)

	const [payee, setPayee] = useState("")
	const [amount, setAmount] = useState("")
	const [useLeadBooker, setUseLeadBooker] = useState(Boolean(leadBooker?.email))
	const [alternateEmail, setAlternateEmail] = useState("")
	const [submitting, setSubmitting] = useState(false)
	const [errors, setErrors] = useState({})
	const [showLinkFallback, setShowLinkFallback] = useState(false)
	const [fallbackReason, setFallbackReason] = useState("")

	const outstandingNumber = Number(outstanding) || 0

	const handleAmountChange = (raw) => {
		if (raw === "" || /^\d*\.?\d{0,2}$/.test(raw)) {
			setAmount(raw)
		}
	}

	const handleSetToOutstanding = () => {
		setAmount(outstandingNumber.toFixed(2))
	}

	const validate = () => {
		const nextErrors = {}
		if (!payee.trim()) nextErrors.payee = "Payee name is required."

		const amountNum = Number(amount)
		if (!amount.trim()) nextErrors.amount = "Amount is required."
		else if (!Number.isFinite(amountNum)) nextErrors.amount = "Amount must be a number."
		else if (amountNum <= 0) nextErrors.amount = "Amount must be greater than zero."
		else if (amountNum > outstandingNumber + 0.001) nextErrors.amount = `Amount can't exceed £${formatGbp(outstandingNumber)}.`

		if (useLeadBooker && !leadBooker?.email) {
			nextErrors.recipient = "Lead booker has no email. Untick and enter an address."
		} else if (!useLeadBooker) {
			const trimmed = alternateEmail.trim()
			if (!trimmed) nextErrors.recipient = "Recipient email is required."
			else if (!EMAIL_RE.test(trimmed)) nextErrors.recipient = "That doesn't look like a valid email address."
		}

		setErrors(nextErrors)
		return Object.keys(nextErrors).length === 0
	}

	const submit = async () => {
		if (!stripe || !elements) return
		if (!validate()) return

		setSubmitting(true)
		setShowLinkFallback(false)
		setFallbackReason("")

		const cardNumberElement = elements.getElement(CardNumberElement)

		// Step 1: create a PaymentMethod from the typed card.
		const pmResult = await stripe.createPaymentMethod({
			type: "card",
			card: cardNumberElement,
			billing_details: { name: payee },
		})

		if (pmResult.error) {
			pushAlert({ type: "ERROR", title: pmResult.error.message || "Card was invalid." })
			setSubmitting(false)
			return
		}

		// Step 2: server-side confirm via callable.
		let result
		try {
			const response = await createPaymentIntentCallable({
				bookingID,
				amount: Number(amount),
				payee: payee.trim(),
				paymentMethodID: pmResult.paymentMethod.id,
				useLeadBooker,
				alternateEmail: useLeadBooker ? undefined : alternateEmail.trim(),
			})
			result = response.data
		} catch (err) {
			const message = err?.message || "Card was declined."
			setFallbackReason(message)
			setShowLinkFallback(true)
			setSubmitting(false)
			return
		}

		if (result.status === "SUCCEEDED") {
			pushAlert({ type: "SUCCESS", title: "Payment taken" })
			close()
			return
		}

		if (result.status === "REQUIRES_ACTION") {
			// Step 3: handle 3DS in-modal via Stripe's iframe.
			const action = await stripe.handleNextAction({ clientSecret: result.clientSecret })

			if (action.error) {
				setFallbackReason(action.error.message || "3D Secure challenge failed.")
				setShowLinkFallback(true)
				setSubmitting(false)
				return
			}

			if (action.paymentIntent?.status === "succeeded") {
				pushAlert({ type: "SUCCESS", title: "Payment taken (3DS approved)" })
				close()
				return
			}

			// Not succeeded — offer the link fallback.
			setFallbackReason("3D Secure challenge could not complete in the modal.")
			setShowLinkFallback(true)
			setSubmitting(false)
			return
		}

		setSubmitting(false)
	}

	const handleFallback = () => {
		close()
		if (typeof onFallbackToLink === "function") {
			onFallbackToLink({
				payee: payee.trim(),
				amount: Number(amount) || undefined,
				useLeadBooker,
				alternateEmail: useLeadBooker ? undefined : alternateEmail.trim(),
			})
		}
	}

	return (
		<Window
			title="Take card (MOTO)"
			className="slim"
			close={() => close()}>
			<WindowCard>
				<div className="input-grid cols-2">
					<div className="span-2">
						<Input
							type="text"
							placeholder="Cardholder name:"
							value={payee}
							onChange={setPayee}
						/>
						{errors.payee && <p className={styles.error}>{errors.payee}</p>}
					</div>

					<div className="span-2">
						<div style={{ display: "flex", gap: 8, alignItems: "stretch" }}>
							<div style={{ flex: 1 }}>
								<Input
									type="text"
									inputMode="decimal"
									placeholder="Amount:"
									symbol="£"
									value={amount}
									onChange={handleAmountChange}
								/>
							</div>
							<Button
								label="Set to outstanding"
								small={true}
								onClick={handleSetToOutstanding}
								disabled={outstandingNumber <= 0}
							/>
						</div>
						{errors.amount && <p className={styles.error}>{errors.amount}</p>}
					</div>

					<div className="span-2">
						<label className={styles.cardLabel}>Card number</label>
						<div className={styles.stripeField}>
							<CardNumberElement options={ELEMENT_OPTIONS} />
						</div>
					</div>

					<div>
						<label className={styles.cardLabel}>Expiry</label>
						<div className={styles.stripeField}>
							<CardExpiryElement options={ELEMENT_OPTIONS} />
						</div>
					</div>

					<div>
						<label className={styles.cardLabel}>CVC</label>
						<div className={styles.stripeField}>
							<CardCvcElement options={ELEMENT_OPTIONS} />
						</div>
					</div>

					<div className="span-2">
						<RecipientPicker
							leadBookerEmail={leadBooker?.email}
							leadBookerLabel={leadBooker?.email}
							useLeadBooker={useLeadBooker}
							onToggle={(next) => {
								setUseLeadBooker(next)
								setErrors((e) => ({ ...e, recipient: undefined }))
							}}
							alternateEmail={alternateEmail}
							onAlternateChange={(v) => {
								setAlternateEmail(v)
								setErrors((e) => ({ ...e, recipient: undefined }))
							}}
							error={errors.recipient}
						/>
					</div>

					{showLinkFallback && (
						<div className={["span-2", styles.fallback].join(" ")}>
							<p className={styles.fallbackReason}>{fallbackReason}</p>
							<Button
								label="Send as payment link instead"
								type="secondary"
								small={true}
								onClick={handleFallback}
							/>
						</div>
					)}

					<div className="span-2">
						<Button
							label={`Charge £${formatGbp(Number(amount) || 0)}`}
							fullWidth={true}
							loading={submitting}
							loadingText="Charging..."
							onClick={submit}
							disabled={!stripe}
						/>
					</div>
				</div>
			</WindowCard>
		</Window>
	)
}
